import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useSWR from 'swr'
import type { ImportSessionUpdatedEvent } from '@/lib/services/websocket'
import { audienceImportSessionsApi } from '@/lib/api'
import type { IdentitySourceOptions } from '@/types/components/audiences/import-session'
import type { GuidedFixCandidateColumn } from '@digital-twins/shared-types'
import { useOptionalImportWizardStableContext } from '../context/ImportWizardContext'
import { useImportSessionWebsocket } from './useImportSessionWebsocket'
import { getAudienceImportIdentitySourceOptionsKey } from '../lib/cache-keys'

const IDENTITY_SOURCE_OPTION_REASONS = new Set([
  'session_updated',
  'session_expired',
  'analyze_completed',
  'analyze_failed',
  'preview_refresh_started',
  'context_materials_cleanup_completed',
  'socket_resynced_after_gap',
  'visibility_resynced_after_gap',
  'socket_subscribed_sync',
])

export function useIdentitySourceOptions(
  sessionId: string,
  canonicalId: string | null,
  options?: {
    enabled?: boolean
    query?: string
    pageSize?: number
    lastMutationRequestId?: string | null
    subscribeToUpdates?: boolean
  },
) {
  const pageSize = options?.pageSize ?? 50
  const ctx = useOptionalImportWizardStableContext()

  // SWR key is always for the first page — stays stable across "show more" clicks
  const key = useMemo(
    () =>
      options?.enabled === false
        ? null
        : getAudienceImportIdentitySourceOptionsKey(
            sessionId,
            canonicalId,
            options?.query ?? '',
            0,
            pageSize,
          ),
    [canonicalId, options?.enabled, options?.query, pageSize, sessionId],
  )

  const sourceOptionsQuery = useSWR<IdentitySourceOptions>(
    key,
    (_key: unknown, fetcherOptions?: { signal?: AbortSignal }) =>
      audienceImportSessionsApi.getIdentitySourceOptions(sessionId, canonicalId!, {
        query: options?.query,
        offset: 0,
        limit: pageSize,
        signal: fetcherOptions?.signal,
      }),
    {
      revalidateOnFocus: false,
      dedupingInterval: 250,
      keepPreviousData: true,
    },
  )

  // --- Client-side page accumulation for "show more" ---
  const [extraCandidates, setExtraCandidates] = useState<Array<GuidedFixCandidateColumn>>([])
  const [extraWindow, setExtraWindow] = useState<IdentitySourceOptions['candidateWindow'] | null>(null)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const prevFirstPageRef = useRef<IdentitySourceOptions | null | undefined>(undefined)

  const firstPageData = sourceOptionsQuery.data ?? null

  // Reset accumulated extras when the first page data changes (revalidation,
  // field switch, query change) so stale pages don't linger.
  useEffect(() => {
    if (prevFirstPageRef.current === undefined) {
      prevFirstPageRef.current = firstPageData
      return
    }
    if (prevFirstPageRef.current !== firstPageData) {
      prevFirstPageRef.current = firstPageData
      setExtraCandidates([])
      setExtraWindow(null)
    }
  }, [firstPageData])

  const resetAccumulation = useCallback(() => {
    setExtraCandidates([])
    setExtraWindow(null)
  }, [])

  // Merged sourceOptions: first page + accumulated extras
  const sourceOptions = useMemo((): IdentitySourceOptions | null => {
    if (!firstPageData) return null
    if (extraCandidates.length === 0) return firstPageData
    const mergedCandidates = [...firstPageData.candidates, ...extraCandidates]
    const latestWindow = extraWindow ?? firstPageData.candidateWindow
    return {
      ...firstPageData,
      candidates: mergedCandidates,
      candidateWindow: {
        ...latestWindow,
        returnedCount: mergedCandidates.length,
        offset: 0,
      },
    }
  }, [firstPageData, extraCandidates, extraWindow])

  const queryRef = useRef(options?.query)
  queryRef.current = options?.query

  const fetchMore = useCallback(async () => {
    if (!firstPageData || isLoadingMore || !canonicalId) return
    const currentWindow = extraWindow ?? firstPageData.candidateWindow
    if (!currentWindow.hasMore) return

    const currentCount = firstPageData.candidates.length + extraCandidates.length
    setIsLoadingMore(true)
    try {
      const response = await audienceImportSessionsApi.getIdentitySourceOptions(
        sessionId,
        canonicalId,
        {
          query: queryRef.current,
          offset: currentCount,
          limit: pageSize,
        },
      )
      setExtraCandidates((prev) => [...prev, ...response.candidates])
      setExtraWindow(response.candidateWindow)
    } finally {
      setIsLoadingMore(false)
    }
  }, [firstPageData, isLoadingMore, canonicalId, extraCandidates.length, extraWindow, sessionId, pageSize])

  // --- Websocket revalidation (unchanged) ---
  const revalidateSourceOptions = useCallback(async () => {
    await sourceOptionsQuery.mutate()
  }, [sourceOptionsQuery])

  const shouldSubscribeToUpdates = options?.subscribeToUpdates !== false
  const insideWizardWithSubscribe = ctx !== null && shouldSubscribeToUpdates
  const sessionIdForWebsocket = insideWizardWithSubscribe
    ? ''
    : shouldSubscribeToUpdates
      ? sessionId
      : ''

  const handlerRef = useRef<(event: ImportSessionUpdatedEvent) => void>(() => {})
  handlerRef.current = (event: ImportSessionUpdatedEvent) => {
    if (!canonicalId || !IDENTITY_SOURCE_OPTION_REASONS.has(event.reason)) {
      return
    }
    void revalidateSourceOptions()
  }
  const stableHandler = useCallback((event: ImportSessionUpdatedEvent) => {
    handlerRef.current(event)
  }, [])

  useEffect(() => {
    if (!ctx || !shouldSubscribeToUpdates) return
    return ctx.registerImportSessionEventHandler(stableHandler)
  }, [ctx, shouldSubscribeToUpdates, stableHandler])

  useImportSessionWebsocket(sessionIdForWebsocket, stableHandler, {
    lastMutationRequestId: options?.lastMutationRequestId ?? null,
  })

  return {
    sourceOptions,
    error: sourceOptionsQuery.error as Error | undefined,
    isLoading: sourceOptionsQuery.isLoading,
    isValidating: sourceOptionsQuery.isValidating,
    isLoadingMore,
    fetchMore,
    resetAccumulation,
    mutate: sourceOptionsQuery.mutate,
  }
}
